import { createAsyncThunk } from "@reduxjs/toolkit";
import { agent, encodeGetParams } from "../../api";
import { apiUrlV1 } from "../../config/config";
import {
  CreateSystemTrigger,
  IReqTriggers,
  ITriggerData,
  SystemTriggerCatalogItem,
  TriggerEdit,
} from "../../entities/trigger";
import { ApiQueryParams } from "../../entities/global";
import { addNotification } from "../Notification/notificateStore";

export const getTriggers = createAsyncThunk(
  'trigger/list',
  async (params?: ApiQueryParams): Promise<IReqTriggers> => {
    const query = encodeGetParams({ ...params })
    const req = await agent.get(`${apiUrlV1}/organization/trigger/list${query}`);
    if (req.ok) {
      const res = await req.json() as IReqTriggers;
      return res;
    }
    return {
      limit: 250,
      page: 1,
      pages: 1,
      total: 0,
      data: []
    };
  }
);

export const postTrigger = createAsyncThunk(
  'trigger/post-trigger',
  async (params: { data: TriggerEdit }, thunksApi): Promise<ITriggerData> => {
    const response = await agent.post(`${apiUrlV1}/organization/trigger/`, {
      body: JSON.stringify(params.data)
    })
    if (response.ok) {
      const data = await response.json() as ITriggerData;
      thunksApi.dispatch(addNotification({ status: "success", text: "Successfully saved" }));
      thunksApi.dispatch(getTriggers());
      return data;
    }
    const result = await response.json() as { message: string }
    thunksApi.dispatch(addNotification({ status: 'error', text: result.message }))
    throw Error(result.message);
  }
)

export const patchTrigger = createAsyncThunk(
  'trigger/patch-trigger',
  async (params: { data: TriggerEdit, id: number }, thunksApi) => {
    try {
      const response = await agent.patch(`${apiUrlV1}/organization/trigger/${params.id}`, {
        body: JSON.stringify(params.data)
      })
      if (response.ok) {
        thunksApi.dispatch(
          addNotification({ status: "success", text: "Successfully saved" })
        );
        thunksApi.dispatch(getTriggers());
      }
      if (!response.ok) {
        const result = await response.json() as { message: string }
        thunksApi.dispatch(addNotification({ status: 'error', text: result.message }))
        throw result.message;
      }
      throw Error("No response");
    } catch (error) {
      throw Error("Request is not found");
    }
  }
)
/**
 * Catalog of internal events a trigger can subscribe to, plus whether this organization already
 * created one for each. Drives the "Add internal trigger" dialog — the field list of every event
 * comes from the backend, so a new internal event needs no change here.
 */
export const getSystemTriggerCatalog = createAsyncThunk(
  'trigger/system-catalog',
  async (): Promise<SystemTriggerCatalogItem[]> => {
    const req = await agent.get(`${apiUrlV1}/organization/trigger/system/catalog`);
    if (req.ok) {
      return await req.json() as SystemTriggerCatalogItem[];
    }
    return [];
  }
);

export const postSystemTrigger = createAsyncThunk(
  'trigger/post-system-trigger',
  async (params: { data: CreateSystemTrigger }, thunksApi): Promise<ITriggerData> => {
    const response = await agent.post(`${apiUrlV1}/organization/trigger/system`, {
      body: JSON.stringify(params.data)
    })
    if (response.ok) {
      const data = await response.json() as ITriggerData;
      thunksApi.dispatch(addNotification({ status: "success", text: "Internal trigger created" }));
      thunksApi.dispatch(getTriggers());
      thunksApi.dispatch(getSystemTriggerCatalog());
      return data;
    }
    const result = await response.json() as { message: string }
    thunksApi.dispatch(addNotification({ status: 'error', text: result.message }))
    throw Error(result.message);
  }
)
